// Time log Week view (architecture sprint-04 §6.2, S6.3; sprint 7 §7.3 original day cards): always 7 rows
// Mon–Sun with week-max bar scaling (approved decision 4).
import { addDays, weekRange, type LocalDate } from '@shared/calendar';
import { ChevronDown, Plus } from 'lucide-react';
import { useState } from 'react';
import { ColorSwatch } from '@/components/ColorSwatch';
import { EntryCard } from '@/components/entries/EntryCard';
import { EntryFormDialog } from '@/components/entries/EntryFormDialog';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useRangeEntries } from '@/hooks/use-entries';
import { cn } from '@/lib/cn';
import { formatDurationHm, formatShortLocalDate, formatWeekHeading } from '@/lib/format';
import { buildWeek, type WeekRow } from '@/lib/week-model';
import type { EntryListItem } from '@/types/entries';
import { LogNavRow } from './LogNavRow';

const DAYS_PER_WEEK = 7;
const PERCENT = 100;

type FormTarget = { mode: 'create'; date: LocalDate } | { mode: 'edit'; entry: EntryListItem } | null;

interface WeekViewProps {
  date: LocalDate;
  today: LocalDate;
  onNavigate: (date: LocalDate) => void;
}

export function WeekView({ date, today, onNavigate }: WeekViewProps): JSX.Element {
  const { start, end } = weekRange(date);
  const entries = useRangeEntries(start, end);
  const [form, setForm] = useState<FormTarget>(null);
  const [expanded, setExpanded] = useState<Set<LocalDate>>(new Set());
  const model = buildWeek(entries.data ?? [], start);
  const thisWeek = today >= start && today <= end;

  function toggleRow(d: LocalDate): void {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(d)) next.delete(d);
      else next.add(d);
      return next;
    });
  }

  return (
    <section>
      <LogNavRow
        unit="week"
        heading={formatWeekHeading(start, end)}
        onPrevious={() => onNavigate(addDays(date, -DAYS_PER_WEEK))}
        onNext={() => onNavigate(addDays(date, DAYS_PER_WEEK))}
        onToday={() => onNavigate(today)}
        todayDisabled={thisWeek}
      />
      <p className="mb-4 font-mono text-sm text-muted-foreground">
        <span aria-label="Week total">{formatDurationHm(model.totalSeconds)}</span> this week
      </p>

      {model.legend.length > 0 && (
        <ul aria-label="Projects this week" className="mb-4 flex flex-wrap gap-x-3 gap-y-1 text-xs text-muted-foreground">
          {model.legend.map((s) => (
            <li key={s.key} className="flex items-center gap-1.5">
              {s.color ? <ColorSwatch hex={s.color} size={6} /> : <span aria-hidden="true" className="inline-block h-1.5 w-1.5 rounded-full bg-muted-foreground/40" />}
              {s.label}
              {s.archived && ' (archived)'}
            </li>
          ))}
        </ul>
      )}

      {entries.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: DAYS_PER_WEEK }, (_, i) => (
            <Skeleton key={i} className="h-16 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <ul className="space-y-1.5" aria-label="Days">
          {model.rows.map((row) => (
            <DayRow
              key={row.date}
              row={row}
              isToday={row.date === today}
              scale={model.scaleSeconds}
              expanded={expanded.has(row.date)}
              onToggle={() => toggleRow(row.date)}
              onAdd={() => setForm({ mode: 'create', date: row.date })}
              onEdit={(entry) => setForm({ mode: 'edit', entry })}
            />
          ))}
        </ul>
      )}

      <EntryFormDialog
        open={form !== null}
        onOpenChange={(open) => {
          if (!open) setForm(null);
        }}
        mode={form?.mode ?? 'create'}
        entry={form?.mode === 'edit' ? form.entry : undefined}
        initialDate={form?.mode === 'create' ? form.date : date}
      />
    </section>
  );
}

interface DayRowProps {
  row: WeekRow;
  isToday: boolean;
  scale: number;
  expanded: boolean;
  onToggle: () => void;
  onAdd: () => void;
  onEdit: (entry: EntryListItem) => void;
}

/** Splits "Wed 7 Oct" into the day name and the short date. */
function splitLabel(label: string): { dayName: string; shortDate: string } {
  const at = label.indexOf(' ');
  return at < 0 ? { dayName: label, shortDate: '' } : { dayName: label.slice(0, at), shortDate: label.slice(at + 1) };
}

function DayRow({ row, isToday, scale, expanded, onToggle, onAdd, onEdit }: DayRowProps): JSX.Element {
  const panelId = `week-day-${row.date}`;
  const label = formatShortLocalDate(row.date);
  const { dayName, shortDate } = splitLabel(label);
  return (
    <li>
      {/* Today: accent border + blue day name instead of a badge (original). */}
      <div className={cn('card p-3 sm:p-4', isToday && 'border-primary/40')}>
        <div className="flex items-center gap-3">
          {/* The chevron sits inside the toggle (nested buttons are invalid), left of the "+". */}
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={panelId}
            aria-label={`${label}, ${formatDurationHm(row.totalSeconds)}`}
            onClick={onToggle}
            className="flex min-w-0 flex-1 cursor-pointer select-none items-center gap-3 rounded-lg text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <span className="w-16 shrink-0">
              <span className={cn('block text-xs font-medium', isToday ? 'text-link' : 'text-muted-foreground')}>{dayName}</span>
              <span className="block text-xs text-muted-foreground">{shortDate}</span>
            </span>
            <span className="min-w-0 flex-1">
              {row.totalSeconds > 0 ? (
                <span className="flex h-1.5 overflow-hidden rounded-full bg-muted">
                  {row.segments.map((s) => (
                    <span
                      key={s.key}
                      title={`${s.label}: ${formatDurationHm(s.seconds)}`}
                      className={cn('h-full', !s.color && 'bg-muted-foreground/40')}
                      style={{ width: `${(s.seconds / scale) * PERCENT}%`, ...(s.color ? { backgroundColor: s.color } : {}) }}
                    />
                  ))}
                </span>
              ) : (
                <span className="text-xs text-muted-foreground">No entries</span>
              )}
            </span>
            {row.totalSeconds > 0 && <span className="shrink-0 font-mono text-sm tabular-nums text-foreground">{formatDurationHm(row.totalSeconds)}</span>}
            <ChevronDown
              aria-hidden="true"
              className={cn('h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-150', expanded && 'rotate-180')}
            />
          </button>
          <Button variant="ghost" size="icon-sm" aria-label={`Add entry on ${label}`} onClick={onAdd}>
            <Plus className="[stroke-width:2]" />
          </Button>
        </div>
      </div>
      {expanded && (
        <div id={panelId} className="mb-1 ml-4 mt-1 space-y-0.5 border-l border-border pl-4 animate-page-in">
          {row.entries.length === 0 ? (
            <p className="px-3 py-2 text-xs text-muted-foreground">No entries</p>
          ) : (
            row.entries.map((entry) => <EntryCard key={entry.id} entry={entry} onEdit={onEdit} />)
          )}
        </div>
      )}
    </li>
  );
}
